import { useState } from "react";
import toast from "react-hot-toast";

import { X, Save, Loader2 } from "lucide-react";

import { useProductViewModel } from "../../../../viewmodels/website/useProductViewModel";
import CategoryAsyncSelect from "./CategoryAsyncSelect";
import SubCategoryAsyncSelect from "./SubCategoryAsyncSelect";

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  createProduct: (data: FormData) => Promise<boolean>;
}

const ProductModal = ({ isOpen, onClose, onSuccess }: ProductModalProps) => {
  const { createProduct } = useProductViewModel();

  const [saving, setSaving] = useState(false);

  //=========================================
  // FORM
  //=========================================

  const [form, setForm] = useState({
    name: "",
    description: "",

    categoryId: "",
    categoryName: "",

    subCategoryId: "",
    subCategoryName: "",

    modelNumber: "",

    capacity: "0",
    capacityText: "",

    division: "0",
    divisionText: "",

    unit: "",
    platformSize: "",

    material: "",
    displayType: "",
    powerSupply: "",
    communication: "",
    ingressProtection: "",
    operatingTemp: "",
    warranty: "",
  });

  //=========================================
  // FILES
  //=========================================

  const [frontImage, setFrontImage] = useState<File | null>(null);

  const [backImage, setBackImage] = useState<File | null>(null);

  const [leftImage, setLeftImage] = useState<File | null>(null);

  const [rightImage, setRightImage] = useState<File | null>(null);

  const [attachment, setAttachment] = useState<File | null>(null);

  //=========================================
  // RESET
  //=========================================

  const resetForm = () => {
    setForm({
      name: "",

      description: "",

      categoryId: "",
      categoryName: "",

      subCategoryId: "",
      subCategoryName: "",

      modelNumber: "",

      capacity: "0",
      capacityText: "",

      division: "0",
      divisionText: "",

      unit: "",

      platformSize: "",

      material: "",

      displayType: "",

      powerSupply: "",

      communication: "",

      ingressProtection: "",

      operatingTemp: "",

      warranty: "",
    });

    setFrontImage(null);

    setBackImage(null);

    setLeftImage(null);

    setRightImage(null);

    setAttachment(null);
  };

  //=========================================
  // CLOSE
  //=========================================

  const closeModal = () => {
    resetForm();

    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-y-auto">
        {/* ================= HEADER ================= */}

        <div className="sticky top-0 bg-white border-b px-6 py-5 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold">Create Product</h2>

            <p className="text-sm text-gray-500 mt-1">
              Fill in the product information.
            </p>
          </div>

          <button
            onClick={closeModal}
            className="p-2 rounded-xl hover:bg-gray-100"
          >
            <X size={22} />
          </button>
        </div>

        {/* ================= BODY ================= */}

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Product Name */}

            <div>
              <label className="block text-sm font-semibold mb-2">
                Product Name *
              </label>

              <input
                value={form.name}
                onChange={(e) =>
                  setForm({
                    ...form,
                    name: e.target.value,
                  })
                }
                className="w-full border rounded-xl px-4 py-3"
                placeholder="Product name"
              />
            </div>

            {/* Model Number */}

            <div>
              <label className="block text-sm font-semibold mb-2">
                Model Number
              </label>

              <input
                value={form.modelNumber}
                onChange={(e) =>
                  setForm({
                    ...form,
                    modelNumber: e.target.value,
                  })
                }
                className="w-full border rounded-xl px-4 py-3"
                placeholder="Model Number"
              />
            </div>

            {/* Category */}

            <div>
              <label className="block text-sm font-semibold mb-2">
                Category *
              </label>

              <CategoryAsyncSelect
                value={
                  form.categoryId
                    ? {
                        value: form.categoryId,
                        label: form.categoryName,
                      }
                    : null
                }
                onChange={(selected) =>
                  setForm({
                    ...form,
                    categoryId: selected?.value ?? "",
                    categoryName: selected?.label ?? "",
                    subCategoryId: "",
                    subCategoryName: "",
                  })
                }
              />
            </div>

            {/* Sub Category */}

            <div>
              <label className="block text-sm font-semibold mb-2">
                Sub Category
              </label>

              <SubCategoryAsyncSelect
                categoryId={form.categoryId}
                value={
                  form.subCategoryId
                    ? {
                        value: form.subCategoryId,
                        label: form.subCategoryName,
                      }
                    : null
                }
                onChange={(selected) =>
                  setForm({
                    ...form,
                    subCategoryId: selected?.value ?? "",
                    subCategoryName: selected?.label ?? "",
                  })
                }
              />
            </div>

            {/* Capacity */}

            <div>
              <label className="block text-sm font-semibold mb-2">
                Capacity
              </label>

              <input
                type="number"
                min={0}
                step="any"
                value={form.capacity}
                onChange={(e) =>
                  setForm({
                    ...form,
                    capacity: e.target.value,
                  })
                }
                className="w-full border rounded-xl px-4 py-3"
              />
            </div>

            {/* Capacity Text */}

            <div>
              <label className="block text-sm font-semibold mb-2">
                Capacity Text
              </label>

              <input
                type="text"
                value={form.capacityText}
                onChange={(e) =>
                  setForm({
                    ...form,
                    capacityText: e.target.value,
                  })
                }
                className="w-full border rounded-xl px-4 py-3"
                placeholder="Example: 30kg / 60kg / 1500kg"
              />
            </div>

            {/* Division */}

            <div>
              <label className="block text-sm font-semibold mb-2">
                Division
              </label>

              <input
                type="number"
                min={0}
                step="any"
                value={form.division}
                onChange={(e) =>
                  setForm({
                    ...form,
                    division: e.target.value,
                  })
                }
                className="w-full border rounded-xl px-4 py-3"
              />
            </div>

            {/* Division Text */}

            <div>
              <label className="block text-sm font-semibold mb-2">
                Division Text
              </label>

              <input
                type="text"
                value={form.divisionText}
                onChange={(e) =>
                  setForm({
                    ...form,
                    divisionText: e.target.value,
                  })
                }
                className="w-full border rounded-xl px-4 py-3"
                placeholder="Example: 5g / 10g / 20g"
              />
            </div>

            {/* Unit */}

            <div>
              <label className="block text-sm font-semibold mb-2">
                Unit
              </label>

              <select
                value={form.unit}
                onChange={(e) =>
                  setForm({
                    ...form,
                    unit: e.target.value,
                  })
                }
                className="w-full border rounded-xl px-4 py-3"
              >
                <option value="">Select Unit</option>
                <option value="kg">kg</option>
                <option value="g">g</option>
                <option value="mg">mg</option>
                <option value="lb">lb</option>
                <option value="oz">oz</option>
                <option value="ton">ton</option>
                <option value="t">t</option>
              </select>
            </div>

            {/* Platform Size */}

            <div>
              <label className="block text-sm font-semibold mb-2">
                Platform Size
              </label>

              <input
                value={form.platformSize}
                onChange={(e) =>
                  setForm({
                    ...form,
                    platformSize: e.target.value,
                  })
                }
                placeholder="Platform Size"
                className="w-full border rounded-xl px-4 py-3"
              />
            </div>

            {/* Material */}

            <div>
              <label className="block text-sm font-semibold mb-2">
                Material
              </label>

              <input
                value={form.material}
                onChange={(e) =>
                  setForm({
                    ...form,
                    material: e.target.value,
                  })
                }
                placeholder="Material"
                className="w-full border rounded-xl px-4 py-3"
              />
            </div>

            {/* Display Type */}

            <div>
              <label className="block text-sm font-semibold mb-2">
                Display Type
              </label>

              <input
                value={form.displayType}
                onChange={(e) =>
                  setForm({
                    ...form,
                    displayType: e.target.value,
                  })
                }
                placeholder="LCD / LED"
                className="w-full border rounded-xl px-4 py-3"
              />
            </div>

            {/* Power Supply */}

            <div>
              <label className="block text-sm font-semibold mb-2">
                Power Supply
              </label>

              <input
                value={form.powerSupply}
                onChange={(e) =>
                  setForm({
                    ...form,
                    powerSupply: e.target.value,
                  })
                }
                placeholder="Power Supply"
                className="w-full border rounded-xl px-4 py-3"
              />
            </div>

            {/* Communication */}

            <div>
              <label className="block text-sm font-semibold mb-2">
                Communication
              </label>

              <input
                value={form.communication}
                onChange={(e) =>
                  setForm({
                    ...form,
                    communication: e.target.value,
                  })
                }
                placeholder="RS232 / USB / Ethernet"
                className="w-full border rounded-xl px-4 py-3"
              />
            </div>

            {/* Ingress Protection */}

            <div>
              <label className="block text-sm font-semibold mb-2">
                Ingress Protection
              </label>

              <input
                value={form.ingressProtection}
                onChange={(e) =>
                  setForm({
                    ...form,
                    ingressProtection: e.target.value,
                  })
                }
                placeholder="IP65"
                className="w-full border rounded-xl px-4 py-3"
              />
            </div>

            {/* Operating Temperature */}

            <div>
              <label className="block text-sm font-semibold mb-2">
                Operating Temperature
              </label>

              <input
                value={form.operatingTemp}
                onChange={(e) =>
                  setForm({
                    ...form,
                    operatingTemp: e.target.value,
                  })
                }
                placeholder="-10°C ~ 40°C"
                className="w-full border rounded-xl px-4 py-3"
              />
            </div>

            {/* Warranty */}

            <div>
              <label className="block text-sm font-semibold mb-2">
                Warranty
              </label>

              <input
                value={form.warranty}
                onChange={(e) =>
                  setForm({
                    ...form,
                    warranty: e.target.value,
                  })
                }
                placeholder="12 Months"
                className="w-full border rounded-xl px-4 py-3"
              />
            </div>
          </div>

          {/* Description */}

          <div className="mt-5">
            <label className="block text-sm font-semibold mb-2">
              Description
            </label>

            <textarea
              rows={5}
              value={form.description}
              onChange={(e) =>
                setForm({
                  ...form,
                  description: e.target.value,
                })
              }
              placeholder="Product description..."
              className="w-full border rounded-xl px-4 py-3 resize-none"
            />
          </div>

          {/* ==========================
              PRODUCT IMAGES
          ========================== */}

          <div className="mt-8">
            <h3 className="text-lg font-semibold mb-4">Product Images</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {/* Front Image */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Front Image
                </label>

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    setFrontImage(e.target.files?.[0] ?? null)
                  }
                  className="w-full border rounded-xl p-3"
                />

                {frontImage && (
                  <img
                    src={URL.createObjectURL(frontImage)}
                    alt="Front"
                    className="mt-3 w-full h-40 object-cover rounded-xl border"
                  />
                )}
              </div>

              {/* Back Image */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Back Image
                </label>

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    setBackImage(e.target.files?.[0] ?? null)
                  }
                  className="w-full border rounded-xl p-3"
                />

                {backImage && (
                  <img
                    src={URL.createObjectURL(backImage)}
                    alt="Back"
                    className="mt-3 w-full h-40 object-cover rounded-xl border"
                  />
                )}
              </div>

              {/* Left Image */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Left Image
                </label>

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    setLeftImage(e.target.files?.[0] ?? null)
                  }
                  className="w-full border rounded-xl p-3"
                />

                {leftImage && (
                  <img
                    src={URL.createObjectURL(leftImage)}
                    alt="Left"
                    className="mt-3 w-full h-40 object-cover rounded-xl border"
                  />
                )}
              </div>

              {/* Right Image */}

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Right Image
                </label>

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    setRightImage(e.target.files?.[0] ?? null)
                  }
                  className="w-full border rounded-xl p-3"
                />

                {rightImage && (
                  <img
                    src={URL.createObjectURL(rightImage)}
                    alt="Right"
                    className="mt-3 w-full h-40 object-cover rounded-xl border"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Attachment */}

          <div className="mt-8">
            <label className="block text-sm font-semibold mb-2">
              Attachment (PDF)
            </label>

            <input
              type="file"
              accept=".pdf"
              onChange={(e) =>
                setAttachment(e.target.files?.[0] ?? null)
              }
              className="w-full border rounded-xl p-3"
            />
          </div>
        </div>

        {/* ================= FOOTER ================= */}

        <div className="sticky bottom-0 bg-white border-t px-6 py-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={closeModal}
            className="px-6 py-3 rounded-xl border hover:bg-slate-50"
          >
            Cancel
          </button>

          <button
            type="button"
            disabled={saving}
            onClick={async () => {
              if (!form.name.trim()) {
                toast.error("Product name is required");
                return;
              }

              if (!form.categoryId) {
                toast.error("Category is required");
                return;
              }

              try {
                setSaving(true);

                const formData = new FormData();

                formData.append("name", form.name);
                formData.append("description", form.description);

                formData.append("categoryId", form.categoryId);
                formData.append("subCategoryId", form.subCategoryId);

                formData.append("modelNumber", form.modelNumber);

                formData.append("capacity", form.capacity);
                formData.append("capacityText", form.capacityText);

                formData.append("division", form.division);
                formData.append("divisionText", form.divisionText);

                formData.append("unit", form.unit);

                formData.append("platformSize", form.platformSize);
                formData.append("material", form.material);
                formData.append("displayType", form.displayType);
                formData.append("powerSupply", form.powerSupply);
                formData.append("communication", form.communication);
                formData.append(
                  "ingressProtection",
                  form.ingressProtection
                );
                formData.append("operatingTemp", form.operatingTemp);
                formData.append("warranty", form.warranty);

                if (frontImage) {
                  formData.append("frontImage", frontImage);
                }

                if (backImage) {
                  formData.append("backImage", backImage);
                }

                if (leftImage) {
                  formData.append("leftImage", leftImage);
                }

                if (rightImage) {
                  formData.append("rightImage", rightImage);
                }

                if (attachment) {
                  formData.append("attachment", attachment);
                }

                const success = await createProduct(formData);

                if (success) {
                  resetForm();

                  onSuccess?.();

                  onClose();
                }
              } finally {
                setSaving(false);
              }
            }}
            className="bg-primary text-white px-6 py-3 rounded-xl flex items-center gap-2 disabled:opacity-60"
          >
            {saving ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={18} />
                Save Product
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductModal;